import { isObject } from "./object.js";
import { setupListKeyboardNavigation } from "./listKeyboardNavigation.js";
import { toString } from "./string.js";
import { useVirtualizer } from "@tanstack/vue-virtual";
import { cloneDeep, intersection, uniqBy, difference } from "lodash-es";
import { ref, computed, watch, onUnmounted, nextTick } from "vue";
const createInternalItems = (items) => {
  let arr = Array.isArray(items) ? items.slice() : [];
  if (arr.length && !isObject(arr[0])) {
    arr = arr.filter((item, i, self) => self.indexOf(item) === i).map((item) => ({ value: item, text: item }));
  }
  return arr;
};
const createInternalValue = (modelValue, valueIsObject, multiple, itemValue, internalItems) => {
  let val = modelValue;
  if (valueIsObject && isObject(modelValue)) {
    if (multiple) {
      val = modelValue.map((v) => v[itemValue]);
    } else {
      val = modelValue ? modelValue[itemValue] : modelValue;
    }
  }
  const arr = Array.isArray(val) ? val : [val];
  return arr;
};
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
const nextLoop = (ms) => new Promise((resolve) => setTimeout(resolve, ms || 0));
const LIST_ITEM_SIZES = { sm: 32, md: 40, lg: 48 };
const LIST_ITEM_CLASS = "clear-both block w-full cursor-pointer whitespace-nowrap py-1 px-3 text-left text-sm font-medium hover:bg-p-blue-10";
const LIST_ITEM_ACTIVE_CLASS = "selected";
const LIST_ITEM_DISABLED_CLASS = "opacity-30 pointer-events-none";
const useSelectList = (props, inputSearch, virtualizerRef, emit) => {
  let navigationSvc = null;
  const internalItems = ref([]);
  const internalValue = ref([]);
  const search = ref("");
  const selectedItems = computed(() => {
    return internalItems.value.filter((item) => internalValue.value.includes(item[props.itemValue]));
  });
  const deselectedItems = computed(() => {
    return internalItems.value.filter((item) => !internalValue.value.includes(item[props.itemValue]));
  });
  const computedItems = computed(() => {
    return cloneDeep(internalItems.value).filter((item) => {
      return String(item[props.itemText]).toLocaleLowerCase().includes(String(search.value).toLocaleLowerCase());
    });
  });
  const computedItemSize = computed(() => props.itemSize ? props.itemSize : LIST_ITEM_SIZES[props.size]);
  const computedInsideSelected = computed(() => {
    const filteredItems = computedItems.value.map((item) => item[props.itemValue]);
    return filteredItems.every((item) => internalValue.value.includes(item));
  });
  const rowVirtualizer = useVirtualizer(
    computed(() => {
      return {
        count: computedItems.value.length,
        getScrollElement: () => virtualizerRef.value,
        getItemKey: (index) => toString(computedItems.value[index][props.itemValue]),
        estimateSize: () => {
          return props.itemSize ? props.itemSize : LIST_ITEM_SIZES[props.size];
        },
        overscan: 10
      };
    })
  );
  const getValue = (index) => {
    var _a;
    return (_a = computedItems.value[index]) == null ? void 0 : _a[props.itemValue];
  };
  const getText = (index) => {
    var _a;
    return (_a = computedItems.value[index]) == null ? void 0 : _a[props.itemText];
  };
  watch(
    () => props.modelValue,
    (nV) => {
      internalValue.value = createInternalValue(
        nV,
        props.valueIsObject,
        props.multiple,
        props.itemValue,
        internalItems.value
      );
    },
    {
      immediate: true,
      deep: true
    }
  );
  watch(search, async (nV) => {
    await scrollScrollerToTop();
    await nextFrame();
    if (!navigationSvc) {
      return;
    }
    const firstItem = navigationSvc.getItems().filter((item) => item.style.transform.includes("translateY(0px)"))[0];
    if (nV) {
      navigationSvc.setFocusedItem(firstItem);
    } else {
      if (props.multiple) {
        navigationSvc.setFocusedItem(firstItem);
      } else {
        getActiveItemIndex() > -1 ? scrollToActiveItem() : navigationSvc.clearFocusedState();
      }
    }
  });
  onUnmounted(() => {
    navigationSvc == null ? void 0 : navigationSvc.destroy();
    navigationSvc = null;
  });
  const putSelectedItemsOnTop = () => {
    if (props.selectedTopShown) {
      const arr = internalItems.value.reduce(
        (acc, curr) => {
          internalValue.value.includes(curr[props.itemValue]) ? acc[0].push(curr) : acc[1].push(curr);
          return acc;
        },
        [[], []]
      );
      internalItems.value = arr[0].sort(
        (a, b) => String(a[props.itemText]).localeCompare(String(b[props.itemText]), void 0, {
          numeric: true,
          sensitivity: "base"
        })
      ).concat(arr[1]);
    }
  };
  const select = async (e, val) => {
    var _a, _b;
    const el = e.currentTarget;
    if (props.multiple && props.searchable && document.activeElement !== ((_a = inputSearch.value) == null ? void 0 : _a.$el.querySelector("input"))) {
      (_b = inputSearch.value) == null ? void 0 : _b.$el.querySelector("input").focus();
    }
    if (props.multiple) {
      if (!isSelected(val)) {
        emit("update:modelValue", toArrOfObjIfNeeded([...internalValue.value, val]));
      } else {
        emit("update:modelValue", toArrOfObjIfNeeded(internalValue.value.filter((item) => item !== val)));
      }
    } else {
      emit("update:modelValue", props.valueIsObject ? getItemByValue(val) : val);
    }
    await nextTick();
    navigationSvc == null ? void 0 : navigationSvc.setFocusedItem(el.parentElement);
    emit("select", val);
  };
  const toArrOfObjIfNeeded = (arr) => {
    return props.valueIsObject ? arr.map((val) => getItemByValue(val)) : arr;
  };
  const getItemByValue = (value) => {
    return internalItems.value.find((item) => item[props.itemValue] === value);
  };
  const isSelected = (val) => {
    return internalValue.value.includes(val);
  };
  const isDisabled = (val) => {
    const disabledProp = props.disabledBy;
    if (val && typeof val === "object" && disabledProp in val) {
      return val[disabledProp];
    }
    return false;
  };
  const setupNavigationSvc = () => {
    if (navigationSvc) {
      navigationSvc.init();
    } else {
      navigationSvc = setupListKeyboardNavigation({
        itemContainer: virtualizerRef.value,
        itemSelector: "[p-select-list-option]",
        itemSelectSelector: `[p-select-list-option-item]`,
        itemFocusClass: "bg-p-blue-10"
      });
    }
  };
  const destroyNavigationSvc = () => {
    navigationSvc == null ? void 0 : navigationSvc.destroy();
    navigationSvc = null;
  };
  const onFocus = async () => {
    setupNavigationSvc();
  };
  const onMousemove = () => {
    navigationSvc == null ? void 0 : navigationSvc.clearFocusedState();
  };
  const scrollScrollerToTop = async () => {
    rowVirtualizer.value.scrollToOffset(0);
    await nextFrame();
  };
  const scrollToActiveItem = async (options) => {
    var _a;
    if (options == null ? void 0 : options.wait) {
      await nextLoop(options.wait);
    }
    const activeIndex = getActiveItemIndex();
    if (virtualizerRef.value && rowVirtualizer.value && activeIndex > -1) {
      rowVirtualizer.value.scrollToIndex(activeIndex, {
        align: "center"
      });
      await nextLoop();
      await nextFrame();
      const activeEl = (_a = virtualizerRef.value) == null ? void 0 : _a.querySelector(`.${LIST_ITEM_ACTIVE_CLASS}`);
      navigationSvc == null ? void 0 : navigationSvc.setFocusedItem(activeEl == null ? void 0 : activeEl.parentNode);
    }
  };
  const getActiveItemIndex = () => {
    return internalItems.value.indexOf(selectedItems.value[0]);
  };
  const selectAll = () => {
    if (!props.multiple) return;
    const selectedItems2 = internalValue.value;
    const deselectedDisabledItemsValues = deselectedItems.value.filter((item) => isDisabled(item)).map((item) => item[props.itemValue]);
    const filteredItems = computedItems.value.map((item) => item[props.itemValue]);
    const uniqueItems = uniqBy([...selectedItems2, ...filteredItems], (item) => item);
    const toEmit = difference(uniqueItems, deselectedDisabledItemsValues);
    emit("update:modelValue", toArrOfObjIfNeeded(toEmit));
  };
  const clearAll = () => {
    search.value = "";
    if (props.multiple) {
      const disabledItemsValues = internalItems.value.filter((item) => isDisabled(item)).map((item) => item[props.itemValue]);
      const selectedItemsValues = internalValue.value;
      const selectedDisabledItems = intersection(disabledItemsValues, selectedItemsValues);
      emit("update:modelValue", toArrOfObjIfNeeded(selectedDisabledItems));
    } else {
      if (!isDisabled(selectedItems.value[0])) {
        emit("update:modelValue", null);
      }
    }
  };
  watch(
    () => props.items,
    (nV) => {
      internalItems.value = createInternalItems(nV);
      if (props.multiple && props.items.length && internalValue.value.length) {
        const internalValueFiltered = internalValue.value.filter((item) => {
          return internalItems.value.some((i) => i[props.itemValue] === item);
        });
        if (internalValueFiltered.length !== internalValue.value.length) {
          console.warn(`[p-select-list] Some values in the modelValue are not in the items list.`);
          emit("update:modelValue", toArrOfObjIfNeeded(internalValueFiltered));
        }
      }
    },
    {
      immediate: true
    }
  );
  return {
    LIST_ITEM_SIZES,
    LIST_ITEM_CLASS,
    LIST_ITEM_ACTIVE_CLASS,
    LIST_ITEM_DISABLED_CLASS,
    selectedItems,
    computedItems,
    computedItemSize,
    computedInsideSelected,
    internalItems,
    internalValue,
    search,
    rowVirtualizer,
    getValue,
    getText,
    isSelected,
    isDisabled,
    setupNavigationSvc,
    destroyNavigationSvc,
    select,
    onFocus,
    onMousemove,
    scrollToActiveItem,
    putSelectedItemsOnTop,
    selectAll,
    clearAll
  };
};
export {
  useSelectList
};
